import { useState, useEffect } from "react";
import styles from "./Step3.module.scss";
import Utilities from "../utils/Utilities";
import { VscEdit } from "react-icons/vsc";
import ExSwitch from "../FormComponents/ExSwitch/ExSwitch.jsx";
import { Radio, FormHelperText } from "@mui/material";
import SelectedServices from "../SelectedServices/SelectedServices";
import ContactForm from "../ContactForm/ContactForm";
import Spinner from "react-bootstrap/Spinner";
import useMobile from "../../hooks/useMobile.js";
import { doBooking } from "../../models/models.js";
import usePreferencesStore from "../../store/preferencesStore.js";
import Select from "react-select";
import FormTextfield from "../FormComponents/FormTextfield/FormTextfield";

function Step3(props) {
  const {
    setIsConfirmButtonEnabled,
    isInFlight,
    confirmButtonClicked,
    setConfirmButtonClicked,
    control,
    trigger,
    watch,
    isValid,
    errorMobile,
    mobile,
    setErrorMobile,
    setMobile,
    privacy,
    setPrivacy,
    tos,
    privacyPolicy,
  } = props;

  const isMobile = useMobile();
  const { preferences } = usePreferencesStore();
  const [isLoading, setIsLoading] = useState(false);
  const [moreThan2Services, setMoreThan2Services] = useState(
    props.selectedServices.length > 2
  );

  // Stati per i dati del form
  const [formData, setFormData] = useState({
    nameFirst: "",
    nameLast: "",
    phone: "",
    contactEmail: "",
    note: "",
  });

  // Log form data changes
  useEffect(() => {
    console.log("Step3 Form Data Updated:", formData);
  }, [formData]);

  // Stati per la gestione delle note
  const [noteState, setNoteState] = useState({
    selectedType: null,
    showFreeNote: false,
  });

  // Log note state changes
  useEffect(() => {
    console.log("Note State Updated:", noteState);
  }, [noteState]);

  const noteOptions = [
    {
      value: "Informazioni su linea fissa",
      label: "Informazioni su linea fissa",
    },
    {
      value: "Informazioni su linea mobile",
      label: "Informazioni su linea mobile",
    },
    { value: "Offerte e promozioni", label: "Offerte e promozioni" },
    {
      value: "Ritiro prodotto prenotato on line",
      label: "Ritiro prodotto prenotato on line",
    },
    { value: "Altro", label: "Altro" },
  ];

  // Handler per i cambiamenti del form con logging
  const handleFormChange = (field, value) => {
    console.log("Form Field Change:", { field, value });
    setFormData((prev) => {
      const newState = {
        ...prev,
        [field]: value,
      };
      console.log("New Form State:", newState);
      return newState;
    });
  };

  // Handler per le note con logging
  const handleNoteChange = (type, value) => {
    console.log("Note Change:", { type, value });
    if (type === "select") {
      setNoteState((prev) => {
        const newState = {
          selectedType: value,
          showFreeNote: value?.value === "Altro",
        };
        console.log("New Note State:", newState);
        return newState;
      });
      if (value?.value !== "Altro") {
        handleFormChange("note", value?.value || "");
      }
    } else if (type === "text") {
      handleFormChange("note", value);
    }
  };

  // Effect for booking
  const handleBooking = async () => {
    setIsLoading(true);
    try {
      await doBooking(
        props.services,
        props.selectedServices,
        props.selectedTime.slotts,
        formData
      );
      props.setFinished(true);
    } catch (error) {
      console.error("Booking Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIsValid = (isValid) => {
    console.log("Form Validation Changed:", { isValid, privacy });
    setIsConfirmButtonEnabled(isValid && privacy);
  };

  const handleConfirmClick = () => {
    setConfirmButtonClicked(true);
    trigger();
    handleBooking();
  };

  if (isLoading) {
    return (
      <div className={styles["saving-overlay"]}>
        <Spinner animation="border" role="status" className={styles.spinner}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className={styles["booking-summary"]}>
      <span className={styles["step-title"]}>Riepilogo Prenotazione</span>

      <div className={styles["selected-date"]}>
        <span>del</span>
        {props.selectedDay && (
          <span className={styles["selected-date_part1"]}>
            {props.selectedDay.getDate()}/{props.selectedDay.getMonth() + 1}/
            {props.selectedDay.getYear() - 100}
          </span>
        )}
        {props.selectedTime && (
          <div className={styles["selected-date_part2"]}>
            <span>
              alle ore{" "}
              <b>
                {Utilities.getEndTime(
                  props.selectedServices,
                  props.selectedTime
                )}
              </b>
            </span>
            <button onClick={props.onBack} className={styles["edit-button"]}>
              <VscEdit className={styles["edit-icon"]} />
            </button>
          </div>
        )}
      </div>

      <div className={styles["selected-services-container"]}>
        <SelectedServices
          services={props.services}
          employees={props.employees}
          data={props.selectedServices}
          showTotal={true}
          moreThan2Services={moreThan2Services}
        />
        {preferences.mostra_prezzo_finale &&
          props.selectedServices.length > 0 && (
            <div className={styles["total-container"]}>
              <div className={styles["total-label"]}>Totale:</div>
              <div className={styles["total-amount"]}>
                {preferences.prefix}{" "}
                {Utilities.getTotal(props.selectedServices, props.services)
                  .toFixed(2)
                  .toString()
                  .replace(".", ",")}{" "}
                €
              </div>
            </div>
          )}
      </div>

      <div className={styles["contact-form-container"]}>
        <ContactForm
          onChangeNameFirst={(value) => handleFormChange("nameFirst", value)}
          onChangeNameLast={(value) => handleFormChange("nameLast", value)}
          onChangePhone={(value) => handleFormChange("phone", value)}
          onChangecontactEmail={(value) =>
            handleFormChange("contactEmail", value)
          }
          namefirst={formData.nameFirst}
          namelast={formData.nameLast}
          phone={formData.phone}
          contactEmail={formData.contactEmail}
          onChangeIsValid={handleIsValid}
          control={control}
          watch={watch}
          isValid={isValid}
          confirmButtonClicked={confirmButtonClicked}
          errorMobile={errorMobile}
          setErrorMobile={setErrorMobile}
          setMobile={setMobile}
          mobile={mobile}
        />
      </div>

      {preferences.hideNotes ? (
        <div className={styles["structured-note-section"]}>
          <Select
            value={noteState.selectedType}
            onChange={(option) => handleNoteChange("select", option)}
            options={noteOptions}
            placeholder="Seleziona tipo nota"
            className={styles["note-select"]}
            classNamePrefix="note-select"
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: "46px",
                borderColor: state.isFocused
                  ? "var(--primaryColor)"
                  : "#e5e5e5",
                boxShadow: state.isFocused
                  ? "0 0 0 1px var(--primaryColor)"
                  : "none",
                "&:hover": {
                  borderColor: "var(--primaryColor)",
                },
              }),
              option: (base, state) => ({
                ...base,
                padding: "12px 16px",
                backgroundColor: state.isSelected
                  ? "var(--primaryColor)"
                  : state.isFocused
                  ? "var(--primaryColor15)"
                  : "white",
                color: state.isSelected ? "white" : "var(--textColor)",
                cursor: "pointer",
              }),
            }}
          />
          {noteState.showFreeNote && (
            <div className={styles["note-field-container"]}>
              <FormTextfield
                label="Scrivi nota"
                type="text"
                formName="note"
                field={{
                  onChange: (e) => handleNoteChange("text", e.target.value),
                  value: formData.note,
                }}
                multiline={true}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={styles["free-note-section"]}>
          <FormTextfield
            label="Nota facoltativa"
            type="text"
            formName="note"
            field={{
              onChange: (e) => handleNoteChange("text", e.target.value),
              value: formData.note,
            }}
            multiline={true}
          />
        </div>
      )}

      <div className={styles["switch-container"]}>
        <ExSwitch
          isEnabled={true}
          switchType={"primary"}
          labelPlacement={"start"}
          onChange={(event) => setPrivacy(event.target.checked)}
        />
        <span className={styles["check-label"]}>
          Confermando la prenotazione accetto i{" "}
          <a href={tos} target="_blank" rel="noreferrer">
            Termini e Condizioni d'Uso
          </a>{" "}
          e la{" "}
          <a href={privacyPolicy} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </span>
      </div>

      <div className={styles["confirm-container"]}>
        <button
          className={styles["confirm-book-button"]}
          onClick={handleConfirmClick}
          disabled={!isValid || !privacy}
        >
          {!isInFlight ? "CONFERMA PRENOTAZIONE" : "Loading..."}
        </button>
      </div>
    </div>
  );
}

export default Step3;
